import { useEffect, useState } from 'react'
import isEmpty from 'lodash-es/isEmpty'

import { useInlineNotifications, useUkTimeZone } from '../../../hooks'
import { parseServerValidation } from '../../../utils/validation'
import { FormikForm } from '../../Formik'
import { getTaskFiles } from '../Task.api'
import { TaskFormYesNoConfirmationModal } from '../TaskFormYesNoConfirmationModal'

import { FormFields } from './FormFields'
import {
  TASK_FORM_FILES_FIELD_NAME,
  TASK_FORM_ID_FIELD_NAME,
  TASK_FORM_INITIAL_VALUES,
  TASK_FORM_MARK_AS_DONE_FIELD_NAME,
  TASK_FORM_YES_NO_CONFIRMATION_MODAL_INITIAL_VALUES,
  TASK_FORM_YES_NO_FIELD_NAME,
} from './TaskForm.constants'
import {
  getTaskFormCreationPayload,
  getTaskFormValidationSchema,
  isTaskFormEditing,
  isTaskFormYesNoConfiguration,
} from './TaskForm.utils'

function TaskForm({
  FormContainer,
  containerProps,
  fieldsProps,
  getPayload = getTaskFormCreationPayload,
  initialValues,
  setModal,
  onCancel,
  onSubmit,
  onSuccess,
}) {
  const [formInitialValues, setFormInitialValues] = useState(
    initialValues || TASK_FORM_INITIAL_VALUES
  )

  const [loadingFiles, setLoadingFiles] = useState(() =>
    isTaskFormEditing(formInitialValues[TASK_FORM_ID_FIELD_NAME])
  )

  const isEditing = isTaskFormEditing(
    formInitialValues[TASK_FORM_ID_FIELD_NAME]
  )

  const { timezone } = useUkTimeZone()

  const { handleAddNotification, handleRemoveNotification, notifications } =
    useInlineNotifications()

  useEffect(() => {
    if (formInitialValues[TASK_FORM_ID_FIELD_NAME]) {
      getTaskFiles(formInitialValues[TASK_FORM_ID_FIELD_NAME])
        .then(({ data }) => {
          const files = data.map(file => ({ ...file, name: file.filename }))

          setFormInitialValues(curInitialValues => ({
            ...curInitialValues,
            [TASK_FORM_FILES_FIELD_NAME]: files,
          }))
        })
        .finally(() => {
          setLoadingFiles(false)
        })
    }
  }, [formInitialValues[TASK_FORM_ID_FIELD_NAME]])

  const validationSchema = getTaskFormValidationSchema(
    formInitialValues,
    isEditing
  )

  function handleSubmit(values, { setErrors, setSubmitting }) {
    function submit(values) {
      const payload = getPayload({
        initialValues: formInitialValues,
        timezone,
        values,
      })

      onSubmit(payload, values[TASK_FORM_ID_FIELD_NAME])
        .then(({ data }) => {
          onSuccess?.(data)
        })
        .catch(({ response }) => {
          if (!isEmpty(response?.data)) {
            setSubmitting(false)
            setErrors(parseServerValidation(response.data))
          }
        })
    }

    function handleCloseYesNoConfirmation() {
      setSubmitting(false)
      setModal(null)
    }

    function handleSubmitYesNoConfirmationModal(yesNoValues) {
      setModal(null)

      submit({ ...values, ...yesNoValues })
    }

    // is need to show yes/no confirmation
    if (
      values[TASK_FORM_MARK_AS_DONE_FIELD_NAME] &&
      values[TASK_FORM_MARK_AS_DONE_FIELD_NAME] !==
        formInitialValues[TASK_FORM_MARK_AS_DONE_FIELD_NAME] &&
      isTaskFormYesNoConfiguration(values)
    ) {
      setModal(
        <TaskFormYesNoConfirmationModal
          initialValues={TASK_FORM_YES_NO_CONFIRMATION_MODAL_INITIAL_VALUES}
          name={TASK_FORM_YES_NO_FIELD_NAME}
          onClose={handleCloseYesNoConfirmation}
          onSubmit={handleSubmitYesNoConfirmationModal}
        />
      )
    } else {
      submit(values)
    }
  }

  return (
    <FormikForm
      enableReinitialize
      Container={FormContainer}
      containerProps={{
        ...containerProps,
      }}
      initialValues={formInitialValues}
      validateOnBlur={false}
      validateOnChange={false}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      <FormFields
        {...fieldsProps}
        isEditing={isEditing}
        loadingFiles={loadingFiles}
        notifications={notifications}
        setModal={setModal}
        onCancel={onCancel}
        onRemoveNotification={handleRemoveNotification}
        onSetInitialValues={setFormInitialValues}
        onSetNotification={handleAddNotification}
      />
    </FormikForm>
  )
}

export default TaskForm
